import React, { useState } from "react";
import { Link } from "react-router-dom";

import {
  Button as MuiButton,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Typography,
  useMediaQuery,
} from "@material-ui/core";
import ContentAdd from "@material-ui/icons/Add";

import {
  Button,
  DateInput,
  email,
  ImageField,
  ImageInput,
  regex,
  required,
  SelectInput,
  TextInput,
  useNotify,
  useRefresh,
} from "react-admin";
import { Form } from "react-final-form";

import {
  AttachPhotoIcon,
  CancelIcon,
  SaveIcon,
} from "../../../../components/common/iconography";
import { logError, safeAPI } from "../../../../utils/appUtils";
import { REGEX_PHONE } from "../../../../utils/constants";
import { blobToBase64, imageFileToJpeg } from "../../../../utils/jpegUtils";
import { CarrierDropDown } from "./CarrierDropDown";

const validateName = [required()];
const validatePhone = [
  required(),
  regex(REGEX_PHONE, "Must be valid Phone No."),
];
const validateEmail = [email()];
const validateLast4 = [
  required(),
  regex(/^[A-Za-z0-9]{4}$/, "The last 4 letters or digits of the number"),
];

const LOCALE_CHOICES = [
  { id: "en", name: "English" },
  { id: "es", name: "Spanish" },
];

// What the manager can do about each refusal, in the words the server's code
// stands for. Anything else shows the server's own message.
const UNREADABLE_FILE =
  "That file can't be read. Use a JPEG or PNG photo of the license.";

const ERROR_HINTS = {
  SCAN_UNREADABLE:
    "Couldn't read that license. Take a clearer photo — flat, in good light, no glare.",
  LICENSE_EXPIRED: "That license is expired. The driver needs a current one.",
  ID_SCAN_REQUIRED: "This yard requires a license photo.",
};

/**
 * Create a driver for someone who cannot sign up in the driver app — no
 * smartphone, a broken camera, a sign-up that keeps failing. driverAdmin's
 * createDriver writes the same records the app's sign-up does, minus the face:
 * face ID needs the driver's own consent, so the driver app offers it to them
 * the next time they check in.
 *
 * The license follows the yard's "license scan required" setting. Required: a
 * photo the server can read. Optional: a photo, the details typed off the
 * card, or nothing. The photo is sent to be read and is never stored.
 *
 * The photo is an ImageInput: a drop zone on a desktop, where the manager may
 * have a scan or a saved photo, and on a phone a tap that opens the rear
 * camera (capture="environment"), for the manager standing with the card in
 * hand. Full screen on a phone, where a dialog box would leave the form
 * scrolling inside its margins.
 */
export const DriverCreateDialog = ({ open, onClose }) => {
  const notify = useNotify();
  const fullScreen = useMediaQuery((theme) => theme.breakpoints.down("xs"));
  const [scanRequired, setScanRequired] = useState(
    window.appUser?.reloadYard?.driverIdScanRequired === true,
  );
  const [manual, setManual] = useState(false);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState(null); // { message, driver? }
  const [created, setCreated] = useState(null); // createDriver's answer
  const [formKey, setFormKey] = useState(0);

  // The form (and the photo in it) is rebuilt through its key.
  const reset = () => {
    setManual(false);
    setProblem(null);
    setCreated(null);
    setFormKey((k) => k + 1);
  };

  const close = () => {
    const didCreate = !!created;
    reset();
    onClose(didCreate);
  };

  // A file the drop zone refused (not an image) never reaches the form.
  const dropped = (accepted, rejected) => {
    if (rejected && rejected.length) setProblem({ message: UNREADABLE_FILE });
    else if (accepted && accepted.length) setProblem(null);
  };

  const handleSubmit = async (values, form) => {
    const photo = values.licensePhoto && values.licensePhoto.rawFile;
    if (scanRequired && !photo) {
      setProblem({ message: ERROR_HINTS.ID_SCAN_REQUIRED });
      return;
    }
    setBusy(true);
    setProblem(null);
    try {
      let license = null;
      if (photo) {
        let jpeg;
        try {
          // Shrunk to what Textract needs, well under the request limit.
          jpeg = await imageFileToJpeg(photo);
        } catch (error) {
          form.change("licensePhoto", null);
          setProblem({ message: UNREADABLE_FILE });
          return;
        }
        license = { image: await blobToBase64(jpeg) };
      } else if (manual) {
        license = {
          manual: {
            expiry: values.idExpiry,
            state: values.idState,
            last4: values.idLast4,
            class: values.idClass,
          },
        };
      }
      const out = await safeAPI.post("transREST", "/driverAdmin/createDriver", {
        body: {
          reloadYardId: window.appUser?.reloadYardId,
          carrierId: values.carrierId,
          firstName: values.firstName,
          lastName: values.lastName,
          phone: values.phone,
          email: values.email || undefined,
          locale: values.locale || "en",
          license,
        },
      });
      setCreated({ ...out, email: values.email || null });
    } catch (error) {
      const data = error?.response?.data;
      const refusal = data?.error;
      if (!refusal?.code) {
        logError(
          "managerApp:carrierUsers:DriverCreateDialog:handleSubmit",
          error,
          notify,
        );
        return;
      }
      if (refusal.code === "SCAN_UNREADABLE") {
        form.change("licensePhoto", null);
        // Where the scan is optional, typing the details is the other way on.
        if (!scanRequired) setManual(true);
      }
      // The yard's setting changed since this page loaded.
      if (refusal.code === "ID_SCAN_REQUIRED") {
        setScanRequired(true);
        setManual(false);
      }
      setProblem({
        message: ERROR_HINTS[refusal.code] || refusal.message,
        driver: refusal.code === "ALREADY_ENROLLED" ? data.driver : null,
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={busy ? undefined : close}
      maxWidth="sm"
      fullWidth
      fullScreen={fullScreen}
      disableEnforceFocus
    >
      <DialogTitle>{created ? "Driver created" : "Create Driver"}</DialogTitle>
      {created ? (
        <>
          <DialogContent>
            <Typography>
              {created.driver.firstName} {created.driver.lastName}
              {created.driver.carrierName
                ? ` · ${created.driver.carrierName}`
                : ""}
            </Typography>
            <Typography
              variant="overline"
              color="textSecondary"
              component="p"
              style={{ marginTop: 16 }}
            >
              Driver code
            </Typography>
            <Typography variant="h3" component="p" style={{ letterSpacing: 4 }}>
              {created.driver.driverCode}
            </Typography>
            <Typography style={{ marginTop: 16 }}>
              Give the driver this code. They check in with their phone number
              and last name, or with this code. They can add face ID from their
              phone at check-in.
            </Typography>
            {created.email && (
              <Typography
                style={{ marginTop: 8 }}
                color={created.emailSent ? "textSecondary" : "error"}
              >
                {created.emailSent
                  ? `The code was emailed to ${created.email}.`
                  : "The email could not be sent. Give the driver the code yourself."}
              </Typography>
            )}
          </DialogContent>
          <DialogActions>
            <MuiButton color="default" onClick={reset}>
              Create another
            </MuiButton>
            <MuiButton
              variant="contained"
              color="primary"
              component={Link}
              to={`/carrierUsers/${created.driver.id}`}
              onClick={() => onClose(true)}
            >
              Open driver
            </MuiButton>
          </DialogActions>
        </>
      ) : (
        <Form
          key={formKey}
          onSubmit={handleSubmit}
          initialValues={{ locale: "en" }}
          render={({ handleSubmit: submit, values }) => (
            // A flex column like the dialog's own paper, so the content
            // scrolls and Cancel / Create stay in view.
            <form onSubmit={submit} noValidate style={FORM_STYLE}>
              <DialogContent>
                <Typography variant="body2" color="textSecondary" gutterBottom>
                  For a driver who can't sign up in the driver app. Drivers who
                  can should sign up themselves.
                </Typography>
                <Grid container spacing={1}>
                  <Grid item xs={12} sm={6}>
                    <TextInput
                      source="firstName"
                      label="First Name"
                      variant="outlined"
                      fullWidth
                      validate={validateName}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextInput
                      source="lastName"
                      label="Last Name"
                      variant="outlined"
                      fullWidth
                      validate={validateName}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextInput
                      source="phone"
                      type="tel"
                      label="Mobile Phone"
                      variant="outlined"
                      fullWidth
                      validate={validatePhone}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <CarrierDropDown
                      source="carrierId"
                      label="Carrier"
                      allowEmpty={false}
                      validate={required()}
                    />
                  </Grid>
                  <Grid item xs={12} sm={values.email ? 8 : 12}>
                    <TextInput
                      source="email"
                      type="email"
                      label="Email (optional)"
                      helperText="We'll email the driver their code"
                      variant="outlined"
                      fullWidth
                      parse={(v) => (v ? v.trim().toLowerCase() : undefined)}
                      validate={validateEmail}
                    />
                  </Grid>
                  {values.email && (
                    <Grid item xs={12} sm={4}>
                      <SelectInput
                        source="locale"
                        label="Email language"
                        choices={LOCALE_CHOICES}
                        variant="outlined"
                        fullWidth
                      />
                    </Grid>
                  )}

                  <Grid item xs={12}>
                    {!manual && (
                      <ImageInput
                        source="licensePhoto"
                        label={`Driver's license ${
                          scanRequired ? "(required)" : "(optional)"
                        }`}
                        accept="image/*"
                        fullWidth
                        disabled={busy}
                        options={{
                          disabled: busy,
                          onDrop: dropped,
                          inputProps: { capture: "environment" },
                        }}
                        placeholder={
                          <p>
                            <AttachPhotoIcon fontSize="large" />
                            <br />
                            Drop a photo of the license here, or click to take
                            or choose one.
                          </p>
                        }
                      >
                        <ImageField source="src" title="title" />
                      </ImageInput>
                    )}
                    <Typography
                      variant="caption"
                      color="textSecondary"
                      component="p"
                      style={{ marginTop: 4 }}
                    >
                      The photo is read and then discarded. Only the details on
                      it are kept.
                    </Typography>
                    {!scanRequired && !values.licensePhoto && (
                      <MuiButton
                        color="default"
                        onClick={() => setManual((m) => !m)}
                        disabled={busy}
                      >
                        {manual
                          ? "Take a photo instead"
                          : "Enter the details by hand instead"}
                      </MuiButton>
                    )}
                  </Grid>
                  {manual && (
                    <>
                      <Grid item xs={12} sm={6}>
                        <DateInput
                          source="idExpiry"
                          label="Expires"
                          variant="outlined"
                          fullWidth
                          validate={required()}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextInput
                          source="idState"
                          label="Issuing State"
                          variant="outlined"
                          fullWidth
                          validate={required()}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextInput
                          source="idLast4"
                          label="Number ends in"
                          variant="outlined"
                          fullWidth
                          validate={validateLast4}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextInput
                          source="idClass"
                          label="Class (optional)"
                          variant="outlined"
                          fullWidth
                        />
                      </Grid>
                    </>
                  )}
                </Grid>
                {problem && (
                  <Typography
                    color="error"
                    role="alert"
                    style={{ marginTop: 8 }}
                  >
                    {problem.message}{" "}
                    {problem.driver && (
                      <Link
                        to={`/carrierUsers/${problem.driver.id}`}
                        onClick={() => onClose(false)}
                      >
                        Open {problem.driver.firstName}{" "}
                        {problem.driver.lastName}
                      </Link>
                    )}
                  </Typography>
                )}
              </DialogContent>
              <DialogActions>
                <Button
                  label="Cancel"
                  color="default"
                  onClick={close}
                  disabled={busy}
                >
                  <CancelIcon />
                </Button>
                <Button
                  label="Create"
                  type="submit"
                  variant="contained"
                  disabled={busy}
                >
                  {busy ? (
                    <CircularProgress size={18} color="inherit" />
                  ) : (
                    <SaveIcon />
                  )}
                </Button>
              </DialogActions>
            </form>
          )}
        />
      )}
    </Dialog>
  );
};

const FORM_STYLE = {
  display: "flex",
  flexDirection: "column",
  flex: "1 1 auto",
  minHeight: 0,
};

/** The Drivers tab's Create: opens the dialog, refreshes the list on a create. */
export const DriverCreateButton = ({ disabled }) => {
  const [open, setOpen] = useState(false);
  const refresh = useRefresh();
  return (
    <>
      <Button
        label="ra.action.create"
        onClick={() => setOpen(true)}
        disabled={disabled}
      >
        <ContentAdd />
      </Button>
      <DriverCreateDialog
        open={open}
        onClose={(didCreate) => {
          setOpen(false);
          if (didCreate) refresh();
        }}
      />
    </>
  );
};
